import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, InputGroup, Alert, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import NumberCellInput from './NumberCellInput';

const labels = {
  Eave: '#39D86F',
  Ridge: '#F30A0A',
  Rake: '#57519C',
  Hip: '#07FFF0',
  Valley: '#D825BB',
  // Other: '#D9E0E8',
};

// Edge labels considered "valid" for the per-edge fire setback inputs.
// Matches the canonical strings written by handleLineLabel and the edge
// detection pipeline; anything else (null, undefined, 'Other', etc.)
// counts as unlabeled. Fire-setback inputs remain the five classic types;
// flashing labels are recognized for "has labeled edge" detection only.
const KNOWN_EDGE_LABELS = [
  ...Object.keys(labels),
  'Wall Flashing',
  'Step Flashing',
];

function Setback({
  segments,
  fireSetbacks,
  setbackMode,
  setbackModeHandler,
  fireSetbackHandler,
}) {
  const edgeLabels = Object.keys(labels).map((label) => label.toLowerCase());
  const edgeValues = edgeLabels.map((label) => fireSetbacks[label]);
  const allEdgesEqual =
    edgeValues.length > 0 &&
    edgeValues.every(
      (value) =>
        value !== undefined &&
        value !== null &&
        Number(value) === Number(edgeValues[0])
    );
  const segmentSetbackValues = Array.isArray(segments)
    ? segments.reduce((values, segment) => {
        const setbacks = Array.isArray(segment?.setbacks)
          ? segment.setbacks
          : [];
        setbacks.forEach((value, index) => {
          const line = segment?.lines?.[index];
          if (line === 'Eave') return;
          if (value !== undefined && value !== null) values.push(value);
        });
        return values;
      }, [])
    : [];
  const allSegmentSetbacksEqual =
    segmentSetbackValues.length > 0 &&
    segmentSetbackValues.every(
      (value) => Number(value) === Number(segmentSetbackValues[0])
    );
  const hasSegmentSetbacks = segmentSetbackValues.length > 0;
  const generalValue =
    setbackMode === 0
      ? allSegmentSetbacksEqual
        ? segmentSetbackValues[0]
        : hasSegmentSetbacks
          ? ''
          : fireSetbacks.general ?? ''
      : allEdgesEqual
        ? edgeValues[0]
        : '';

  const handleRadioButtonChange = (value) => setbackModeHandler(value);

  // Edge Label Setbacks (setbackMode === 1) only make sense after the
  // segments' edges have been classified — either by running edge
  // detection from the Roof Segments accordion or by labelling edges
  // manually. Without any labels, the per-edge inputs would just write
  // to a fireSetbacks dictionary that no segment would consume on the
  // next setback recompute (index.js:4532-4536 only applies a setback
  // when seg.lines[i] matches a known label). Disable the inputs and
  // show a guidance alert so the user knows what to do.
  const hasAnyLabeledEdge =
    Array.isArray(segments) &&
    segments.some(
      (segment) =>
        Array.isArray(segment?.lines) &&
        segment.lines.some((line) => KNOWN_EDGE_LABELS.includes(line))
    );
  const edgeLabelInputsDisabled = setbackMode === 1 && !hasAnyLabeledEdge;

  return (
    <div>
      <ToggleButtonGroup
        className="mb-3 btn-block"
        type="radio"
        name="options"
        value={setbackMode}
        onChange={handleRadioButtonChange}
      >
        <ToggleButton variant="primary" style={{ color: setbackMode === 0 ? 'white' : 'gray' }} value={0}>General Fire Setback</ToggleButton>
        <ToggleButton variant="primary" style={{ color: setbackMode === 1 ? 'white' : 'gray' }} value={1}>Edge Label Setbacks</ToggleButton>
      </ToggleButtonGroup>
      {(setbackMode === 0) && (
        <InputGroup size="sm" className="w-75">
          <NumberCellInput
            id="general"
            min={0}
            value={generalValue}
            skipEmptyCommit
            onCommit={(v) => fireSetbackHandler(v, 'general')}
          />
          <InputGroup.Append>
            <InputGroup.Text>inches</InputGroup.Text>
          </InputGroup.Append>
        </InputGroup>
      )}
      {setbackMode === 1 && edgeLabelInputsDisabled && (
        <Alert variant="warning" className="mb-2 w-75 py-2">
          Run <strong>Edge Detection</strong> from the Roof Segments
          accordion, or label roof segment edges manually, to enable
          these setbacks.
        </Alert>
      )}
      {(setbackMode === 1) && Object.entries(labels).map(([label, color]) => {
        const lbl = label.toLowerCase();
        return (
          <Row key={lbl}>
            <Col>
              <InputGroup size="sm" className="mb-2 w-75">
                <InputGroup.Prepend>
                  <InputGroup.Text
                    className="text-white text-shadow"
                    style={{ backgroundColor: color, width: 60 }}
                  >
                    {label}
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <NumberCellInput
                  id={lbl}
                  value={fireSetbacks[lbl] ?? 0}
                  disabled={edgeLabelInputsDisabled}
                  skipEmptyCommit
                  onCommit={(v) => fireSetbackHandler(v, lbl)}
                />
                <InputGroup.Append>
                  <InputGroup.Text>inches</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </Col>
          </Row>
        );
      })}
    </div>
  );
}

Setback.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.object).isRequired,
  fireSetbacks: PropTypes.shape({
    general: PropTypes.number,
    eave: PropTypes.number,
    ridge: PropTypes.number,
    rake: PropTypes.number,
    hip: PropTypes.number,
    valley: PropTypes.number,
  }).isRequired,
  setbackMode: PropTypes.number.isRequired,
  setbackModeHandler: PropTypes.func.isRequired,
  fireSetbackHandler: PropTypes.func.isRequired,
};

export default Setback;
